<template>
    <!-- Error resolve confirmation -->
    <b-modal :id="modalId" hide-footer title="Was the error resolved?">
        <checkbox v-model="wasResolved">
            Confirming that the reported error was resolved
        </checkbox>
        <br />
        <NotesForm :bodyRequired="false" :entityId="dispenseError && +dispenseError.id"
            :entityType="entityType"
            :placeholder="'Introduce a note with additional information (not required)'"
            :prefixNote="`${wasResolved ? '** RESOLVED **' : ''}`" :saveButtonLabel="saveButtonLabel" :silent="true"
            @input="noteChanged" @saved="onSaved" />
    </b-modal>
</template>

<script lang="ts">
// The dispense-error save-confirmation / resolve modal, extracted from PrescriptionDetailsPage.
// It owns the resolved/error-note state and the save-button-label logic.
//
// Contract: read-only entity prop in, scoped events up.
//  - The modal does NOT POST the resolve. On a resolved close it emits "resolved" with the error id;
//    the container carries that id on the SaveIntent and the save orchestrator resolves the error
//    ONLY on the post-save success path. A failed save therefore leaves the error open instead of
//    resolved against an Rx that was never saved.
//  - On an unresolved close it emits "proceed" and the container saves without resolving.
//  - The container keeps ownership of the fetched dispenseError, the dispenseErrors alert array
//    (refetched post-save via refreshDispenseErrors), the SaveIntent (carrying resolveDispenseErrorId),
//    and dispenseErrorLabel.
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { DispenseError } from "@/models/DispenseError";
import { EntityType } from "@/models/EntityType";
import { Note } from "@/models/Note";
import NotesForm from "@/components/Note/NotesForm.vue";
import Checkbox from "@/components/Inputs/Checkbox.vue";

@Component({
    components: {
        NotesForm,
        Checkbox,
    },
})
export default class DispenseErrorResolveModal extends Vue {
    /** The fetched dispense error this modal is armed against (never the route param). */
    @Prop({ required: true }) dispenseError!: DispenseError;
    /** Configuration-driven label ("Error" by default), passed by the container for parity with the page. */
    @Prop({ default: "Error" }) dispenseErrorLabel!: string;

    private readonly modalId = "modal_save_confirmation";
    private readonly entityType = EntityType.DispenseError;
    private wasResolved = false;
    private errorNote: Note = {} as Note;
    private saveButtonLabel = "Save changes";

    show(): void {
        // Reset per-open state: the modal is a single long-lived instance in the parent (no v-if),
        // so a prior resolve must not leave the checkbox pre-checked and re-fire an unintended resolve.
        this.wasResolved = false;
        this.errorNote = {} as Note;
        this.saveButtonLabel = "Save changes";
        this.$bvModal.show(this.modalId);
    }

    hide(): void {
        this.$bvModal.hide(this.modalId);
    }

    // NotesForm "@saved": hand the resolve decision back to the container. The modal does NOT POST
    // the resolve — on a resolved close it emits "resolved" with the error id, and the container
    // carries that id on the SaveIntent so the orchestrator resolves ONLY after the save succeeds.
    // A failed save then leaves the error open instead of resolved against an Rx that never saved.
    onSaved(): void {
        if (this.wasResolved) {
            this.$emit("resolved", { resolvedErrorId: this.dispenseError.id });
        } else {
            this.$emit("proceed");
        }
        this.hide();
    }

    noteChanged(newNote: string): void {
        this.errorNote.body = newNote;
        this.updateSaveButtonLabel();
    }

    @Watch("wasResolved")
    wasResolvedChanged(): void {
        this.updateSaveButtonLabel();
    }

    updateSaveButtonLabel(): void {
        const hasNote = !!this.errorNote.body;
        if (hasNote && this.wasResolved) {
            this.saveButtonLabel = "Save changes, add note and resolve";
        } else if (this.wasResolved) {
            this.saveButtonLabel = "Save changes and resolve";
        } else if (hasNote) {
            this.saveButtonLabel = "Save changes and add note";
        } else {
            this.saveButtonLabel = "Save changes";
        }
    }
}
</script>
